/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import {
    CategoryHierarchy as CategoryHierarchyData,
    IProductRefinerHierarchy,
    IProductsDimensionsAvailabilities
} from '@msdyn365-commerce/commerce-entities';
import MsDyn365 from '@msdyn365-commerce/core';
import {
    format,
    ProductPrice,
    ProductRefinerValue,
    ProjectionDomain,
    ProductSearchResult,
    SortColumn,
    TextValueTranslation,
    DiscountLine
} from '@msdyn365-commerce/retail-proxy';
import {
    getActivePricesAsync,
    getEstimatedAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    LabeledDropdown,
    Waiting
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    buildListPageUrl,
    getCollectionProducts,
    getCollectionRefinersAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    RefinersByCollectionInput,
    sortOptions
} from './actions';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import { RESTORE_CHUNK_SIZE, IFullProductsSearchResultsWithCount } from './actions/get-full-products-by-collection';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    isRangeType,
    Link,
    mergeRefiners,
    ModalToggle,
    ProductSearchResultItems,
    SearchResultModal,
    Separator,
    Title
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { IDobbiesSearchResultContainerData } from './dobbies-search-result-container.data';
import { expandRefinersCount, IDobbiesSearchResultContainerProps } from './dobbies-search-result-container.props.autogenerated';
import { ProductRefinerSource } from '@msdyn365-commerce/retail-proxy';
import { UNXProductIdInput } from '../../themes/dobbies/actions/unx-req-id-data.action';
import { _getScreenSize } from '../../themes/dobbies/utilities/display-utils';
// import listPageState from './list-page-state';
// import { useState, useEffect } from  "react";
export interface ISearchResultContainerViewProps extends IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData> {
    products?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    TitleViewProps: ITitleViewProps;
    categoryHierarchy: ICategoryHierarchyViewProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    CategoryNavContainer: INodeProps;
    RefineAndProductSectionContainer: INodeProps;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    refinersRef: React.RefObject<HTMLDivElement>;
    onMoreFiltersClicked?(keyName: string | undefined, isExpanded: boolean, isFromMoreFilters?: boolean): void;
    totalProductCount?: number;
    onClearFilters?(): void;
    selectedRefiners?: ProductRefinerValue[];
    /** True if the price range filter has a validation error (min > max) */
    hasRangeValidationError?: boolean;
    selectedSortOption?: ILabeledDropdownOption;
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
    endingRefiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
    sortOptions?: ILabeledDropdownOption[];
    selectedSortOption?: ILabeledDropdownOption;
    onSortOptionSelect?(option: ILabeledDropdownOption): void;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[];
    originalRefiners: IProductRefinerHierarchy[];
    discountLines: (DiscountLine[] | undefined)[];
    showProdPrice: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    isUpdated: boolean;
    nonClubPlusDiscount: ProductPrice[];
    updatedProducts: number[] | undefined;
    lastUrlStr: string;
    refinersExpandedStateData?: IDobbiesRefinerExpandedState[][];
    /** Simple map of refiner KeyName to expanded state - used for ALL refiners */
    refinerExpandedStates: Map<string, boolean>;
    shouldShowPLPAds?: boolean;
    isLoadMoreLoading?: boolean;
    isLoadMoreTriggered?: boolean;
    currentInteractingFilterGroup: string | null;
    refinersSnapshot: IProductRefinerHierarchy[];
    /** Key of the filter value being deselected, used to restore state from history */
    pendingDeselectionKey: string | null;
    /** True if the range refiner has a validation error */
    hasRangeValidationError: boolean;
    isFetchingInventory: boolean;
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

export interface IDobbiesRefinerExpandedState {
    refinerId?: number;
    isExpanded: boolean;
    keyName?: string;
}

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>,
    ISearchResultContainerState
> {
    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm' || this._viewport === 'md';
    }

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc },
        { key: sortOptions.sortByOptionNameAsc, value: this.props.resources.sortByOptionNameAsc },
        { key: sortOptions.sortByOptionNameDesc, value: this.props.resources.sortByOptionNameDesc },
        { key: sortOptions.sortByOptionPriceAsc, value: this.props.resources.sortByOptionPriceAsc },
        { key: sortOptions.sortByOptionPriceDesc, value: this.props.resources.sortByOptionPriceDesc },
        { key: sortOptions.sortByOptionRatingDesc, value: this.props.resources.sortByOptionRatingDesc }
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: string | undefined = this.props.context.request.urlTokens.pageType;

    @observable
    private _viewport: GridSize;

    @observable
    private _sortingVersion: number = 0;

    @observable
    private initialTotalProductCount: number | undefined = undefined;

    private _isFetching: boolean = false;
    private readonly _isMobileDevice: boolean = false;
    private readonly _getItemsPerPage = (): number => {
        if (this._isMobileDevice) {
            return this.props.context.app.config.itemsPerPageMobile || 20;
        }

        return this.props.context.app.config.itemsPerPage || 60;
    };

    private _refinersInitialized: boolean = false;

    private _reactionDisposer: (() => void) | null = null;
    private _scrollReapplyRaf: number | null = null;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private productPrices: ProductPrice[];

    private refinersRef: React.RefObject<HTMLDivElement>;

    /**
     * Hash table to store refiners state history.
     * Key: Unique identifier for each filter selection (RefinerRecordId_LeftValueBoundString)
     * Value: Object containing refiners state and product count at the time of selection
     */
    private _refinersStateHistory: Map<
        string,
        {
            refiners: IProductRefinerHierarchy[];
            productCount: number;
        }
    > = new Map();

    /**
     * Tracks if we're in a deselection operation - used synchronously in reaction handler
     */
    private _pendingDeselection: {
        key: string;
        savedRefiners: IProductRefinerHierarchy[];
        savedProductCount: number;
    } | null = null;

    /**
     * Cache for product counts by filter combination.
     * Key: Sorted stringified active filters (to ensure consistent keys regardless of selection order)
     * Value: Total product count for that filter combination
     * This cache survives "clear all" to handle API caching where metadata.count is undefined
     */
    private _productCountCache: Map<string, number> = new Map();

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        const mobile = _getScreenSize(this.props.context);
        this._isMobileDevice = mobile === 1;
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];
        this.refinersRef = React.createRef();
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this._getCurrentlySelectedOption() || this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: [],
            originalRefiners: [],
            discountLines: [],
            showProdPrice: false,
            isUpdated: false,
            nonClubPlusDiscount: [],
            updatedProducts: [],
            lastUrlStr: '',
            isLoadMoreLoading: false,
            isLoadMoreTriggered: false,
            currentInteractingFilterGroup: null,
            refinersSnapshot: [],
            pendingDeselectionKey: null,
            hasRangeValidationError: false,
            refinerExpandedStates: new Map<string, boolean>(),
            isFetchingInventory: true
        };
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;

        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        // VSI-Customization -- START check & redirect if found url in returns of search query
        if (props.data.products.result && props.data.products.result.products?.length) {
            const redirectUrl = props.data.products.result.products[0].ExtensionProperties?.find(item => item.Key === 'RedirectURL');
            if (redirectUrl && redirectUrl.Value!.StringValue && MsDyn365.isBrowser && window) {
                window.location.assign(redirectUrl.Value!.StringValue);
            }
        }
        /* VSI-Customization -- END  */

        // Initalization of list page
        this.props.data.products.then(products => {
            this.props.data.listPageState.then(listPageState => {
                if (this._pageType === 'Category') {
                    listPageState.pageType = 'Category';
                } else {
                    listPageState.pageType = 'Search';
                }

                let querySorting: SortColumn[] = [];

                if (this.props.context.request.query && this.props.context.request.query.sorting) {
                    querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
                }

                listPageState.currentPageNumber =
                    (this.props.context.request.query && +this.props.context.request.query.skip / this._getItemsPerPage()) || 0;
                listPageState.sortingCritera = { Columns: querySorting };
                listPageState.activeProducts = products.products || [];
                listPageState.totalProductCount = products.count;
                // Save initial product count for restoring when all filters are cleared
                this.initialTotalProductCount = listPageState.totalProductCount;
                listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);

                // Initialize reaction based on listPageState properties
                // Use _sortingVersion (MobX observable) instead of this.state.isUpdated (React state)
                this._reactionDisposer = reaction(
                    () => {
                        return [
                            listPageState.activeFilters && listPageState.activeFilters.length,
                            listPageState.currentPageNumber,
                            this._sortingVersion
                        ];
                    },
                    () => {
                        // Guard against concurrent fetches
                        if (this._isFetching) {
                            return;
                        }
                        this._isFetching = true;
                        // Generate cache key for current filter combination BEFORE async calls
                        const filterCacheKey = this._getFilterCombinationKey(listPageState.activeFilters || undefined);
                        const input = new GetFullProductsByCollectionInput(
                            listPageState.pageType,
                            this.props.context.request.apiSettings,
                            {
                                Paging: {
                                    Top: this._getItemsPerPage(),
                                    Skip: Math.max(listPageState.pageSize * (listPageState.currentPageNumber || 0), 0)
                                },
                                count: true,
                                Sorting: listPageState.sortingCritera || {}
                            },
                            listPageState.activeFilters || [],
                            +(this.props.context.request.urlTokens.itemId || 0),

                            this.props.context.request.query && this.props.context.request.query.q
                                ? this.props.context.request.query.q
                                : JSON.stringify({
                                      ProductId: this.props.context.request.query?.productId,
                                      Recommendation: this.props.context.request.query?.recommendation
                                  }),
                            !!(
                                this.props &&
                                this.props.config &&
                                this.props.config.includeAttributes !== undefined &&
                                this.props.config.includeAttributes
                            ),
                            this.props.config.updateRefinerPanel,
                            this.props.context.request.locale
                        );
                        getCollectionProducts(input, this.props.context.actionContext)
                            .then(async productResults => {
                                transaction(() => {
                                    if ((listPageState.currentPageNumber || 0) === 0) {
                                        listPageState.activeProducts = productResults.products;
                                    } else {
                                        const existingProducts = listPageState.activeProducts || [];
                                        const mergedProducts = [...existingProducts, ...productResults.products];
                                        const uniqueProducts = Array.from(
                                            new Map(mergedProducts.map(product => [product.RecordId, product])).values()
                                        );
                                        listPageState.activeProducts = uniqueProducts;
                                    }
                                    // Simple count logic: use API count if it's a number (including 0), otherwise use cache
                                    const apiCount = productResults.count;
                                    const hasProducts = productResults.products && productResults.products.length > 0;

                                    if (typeof apiCount === 'number') {
                                        // API returned count (including 0) - use it and cache it
                                        listPageState.totalProductCount = apiCount;
                                        this._productCountCache.set(filterCacheKey, apiCount);
                                    } else if (!hasProducts) {
                                        // No products returned and no API count - show 0
                                        listPageState.totalProductCount = 0;
                                        this._productCountCache.set(filterCacheKey, 0);
                                    } else {
                                        // Has products but no API count - use cached count if available
                                        const cachedCount = this._productCountCache.get(filterCacheKey);
                                        if (typeof cachedCount === 'number') {
                                            listPageState.totalProductCount = cachedCount;
                                        }
                                        // If no cache either, keep the existing count unchanged
                                    }
                                });
                                this._isFetching = false;
                                if (this.props.config.enableAffiliationBasedPricing) {
                                    this._getActivePrice();
                                }
                                await this.getCNCInventory();
                            })
                            .catch(() => {
                                this._isFetching = false;
                            });

                        if (this.props.config.updateRefinerPanel) {
                            const refinerInput = new RefinersByCollectionInput(
                                listPageState.pageType,
                                this.props.context.request.apiSettings,
                                {
                                    Paging: {
                                        Top: this._getItemsPerPage(),
                                        Skip: 0
                                    },
                                    count: true,
                                    Sorting: listPageState.sortingCritera ?? {}
                                },
                                listPageState.activeFilters ?? [],
                                +(this.props.context.request.urlTokens.itemId ?? 0),
                                this.props.context.request.query?.q
                                    ? this.props.context.request.query.q
                                    : this.props.context.request.query?.productId,
                                false,
                                this.props.config.updateRefinerPanel
                            );

                            getCollectionRefinersAction(refinerInput, this.props.context.actionContext)
                                .then(refiners => {
                                    // Get original refiners from state or from initial data
                                    const originalRefiners =
                                        this.state.originalRefiners.length > 0
                                            ? this.state.originalRefiners
                                            : this.props.data.refiners.result || [];

                                    // Store original refiners if not already stored
                                    if (this.state.originalRefiners.length === 0 && this.props.data.refiners.result) {
                                        this.setState({ originalRefiners: this.props.data.refiners.result });
                                    }

                                    // Check if we're handling a deselection with saved state
                                    // Use class property for synchronous access (React setState is async)
                                    if (this._pendingDeselection) {
                                        // DESELECTION: Use the saved refiners state from hash table
                                        // Update counts from API but keep the structure from saved state
                                        const restoredRefiners = this._mergeWithApiCounts(
                                            this._pendingDeselection.savedRefiners,
                                            refiners,
                                            listPageState.activeFilters || []
                                        );
                                        // Clear the pending deselection
                                        // Note: Don't restore savedProductCount - the API returns the correct count
                                        // for the current filter combination
                                        this._pendingDeselection = null;
                                        this.setState({
                                            refiners: restoredRefiners,
                                            pendingDeselectionKey: null,
                                            currentInteractingFilterGroup: null
                                        });
                                    } else if (listPageState.activeFilters && listPageState.activeFilters.length === 0) {
                                        // No filters, use original refiners
                                        this.setState({ refiners: originalRefiners });
                                    } else {
                                        // SELECTION: Merge refiners to preserve current interacting filter group values
                                        const snapshotRefiners =
                                            this.state.refinersSnapshot.length > 0
                                                ? this.state.refinersSnapshot
                                                : this.state.refiners.length > 0
                                                ? this.state.refiners
                                                : originalRefiners;
                                        const mergedRefiners = mergeRefiners(
                                            snapshotRefiners,
                                            refiners,
                                            this.state.currentInteractingFilterGroup
                                        );
                                        this.setState({ refiners: mergedRefiners });
                                    }
                                })
                                .catch(error => {
                                    this.props.telemetry.error(error);
                                });
                        }
                    },
                    { fireImmediately: false }
                );
            });
        });

        if (this.props.context.request.query?.productId) {
            this.props.data.featureProduct.then(products => {
                this.props.data.listPageState.then(listPageStateFeature => {
                    listPageStateFeature.featureProduct = products;
                });
            });
        }
        this._updateViewport();
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser && window && window && window.addEventListener) {
            const { data } = this.props;
            const categoryHierarchy = data.categoryHierarchy.result;
            const hasRefiners = window.location.search.includes('refiners');
            if (!hasRefiners) {
                sessionStorage.setItem('categoryHierarchy', JSON.stringify(categoryHierarchy));
            }

            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();

            try {
                if ('scrollRestoration' in window.history) {
                    window.history.scrollRestoration = 'manual';
                }
            } catch {
                // ignore unsupported environments
            }
            window.addEventListener('click', this._saveScrollOnProductClick, true);

            // Store original refiners for multi-select filter preservation
            if (data.refiners.result && data.refiners.result.length > 0 && this.state.originalRefiners.length === 0) {
                this.setState({ originalRefiners: data.refiners.result });
            }
        }
        if (this.props.config.enableAffiliationBasedPricing) {
            await this._getActivePrice();
        }
        await this.getNonClubPlusDiscountPrices();
        reaction(
            () => this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts,
            async () => {
                const url = MsDyn365.isBrowser && window && window.location.href;
                if (this.props.data.listPageState.result?.activeProducts.length) {
                    await this.getNonClubPlusDiscountPrices();
                    this.setState({ lastUrlStr: url ? url : '' });
                }
            }
        );
        if (MsDyn365.isBrowser) {
            setTimeout(() => {
                this.setState({ shouldShowPLPAds: true });
            }, 800);
        }

        const prods = this.props.data.products.result?.products;
        if (prods?.length) {
            const unxReqId = prods[0].ExtensionProperties?.find(ep => ep.Key === 'Unx-Request-Id')?.Value?.StringValue;
            this.props.context.actionContext.update(new UNXProductIdInput(), { unxReqId: unxReqId });
        }

        const hydratedFromCache = await this._restoreProductsFromCache();
        if (!hydratedFromCache) {
            await this._restoreLoadedProducts();
        }
        await this.getCNCInventory();
        this._restoreScrollPosition();
    }

    public checkDifference = (array1: number[] | undefined, array2: number[] | undefined) => {
        if (!array1 || !array2) {
            return true;
        }
        if (array1?.length !== array2?.length) {
            return true;
        }
        for (let i = 0; i < array1.length; i++) {
            if (array1[i] !== array2[i]) {
                return true;
            }
        }
        return false;
    };

    public getCNCInventory = async (): Promise<void> => {
        try {
            const listPageState = await this.props.data.listPageState;
            const activeProducts = listPageState.activeProducts;
            // fetch Click and Collect Inventory (all warehouses)
            const productIds = activeProducts?.map(product => product.RecordId) || [];
            const cncProductAvailabilities = await getEstimatedAvailabilityAsync(
                { callerContext: this.props.context.actionContext },
                { ProductIds: productIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
            );

            const productsWithCncInventory = activeProducts?.map(product => {
                const cncInventory = cncProductAvailabilities.AggregatedProductInventoryAvailabilities?.find(productInventory => {
                    return (
                        productInventory.ProductId === product.RecordId && productInventory.PhysicalAvailableInventoryLevelCode !== 'OOS'
                    );
                });

                return {
                    ...product,
                    ProductClickAndCollectInventory: cncInventory || undefined
                };
            });
            transaction(() => {
                listPageState.activeProducts = productsWithCncInventory || [];
            });
        } catch (error) {
            console.error('Failed to load CNC inventory', error);
        } finally {
            this.setState({ isFetchingInventory: false });
        }
    };

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
            window.removeEventListener('click', this._saveScrollOnProductClick, true);
            if (this._scrollReapplyRaf !== null) {
                window.cancelAnimationFrame(this._scrollReapplyRaf);
                this._scrollReapplyRaf = null;
            }
        }
        // Dispose the MobX reaction to prevent memory leaks
        if (this._reactionDisposer) {
            this._reactionDisposer();
            this._reactionDisposer = null;
        }
    }

    private readonly _restoreLoadedProducts = async (): Promise<void> => {
        if (!MsDyn365.isBrowser) {
            return;
        }
        const skip = +(this.props.context.request.query?.skip || 0);
        if (isNaN(skip) || skip <= 0) {
            return;
        }
        const listPageState = await this.props.data.listPageState;
        if (!listPageState) {
            return;
        }
        if ((listPageState.activeProducts?.length || 0) >= skip) {
            return;
        }

        const searchText = this.props.context.request.query?.q
            ? this.props.context.request.query.q
            : JSON.stringify({
                  ProductId: this.props.context.request.query?.productId,
                  Recommendation: this.props.context.request.query?.recommendation
              });
        const includeAttributes = !!(
            this.props &&
            this.props.config &&
            this.props.config.includeAttributes !== undefined &&
            this.props.config.includeAttributes
        );

        const offsets: number[] = [];
        for (let offset = 0; offset < skip; offset += RESTORE_CHUNK_SIZE) {
            offsets.push(offset);
        }

        const buildInput = (offset: number): GetFullProductsByCollectionInput =>
            new GetFullProductsByCollectionInput(
                listPageState.pageType,
                this.props.context.request.apiSettings,
                {
                    Paging: { Top: Math.min(skip - offset, RESTORE_CHUNK_SIZE), Skip: offset },
                    count: true,
                    Sorting: listPageState.sortingCritera || {}
                },
                listPageState.activeFilters || [],
                +(this.props.context.request.urlTokens.itemId || 0),
                searchText,
                includeAttributes,
                this.props.config.updateRefinerPanel,
                this.props.context.request.locale
            );

        let chunkResults: (IFullProductsSearchResultsWithCount | undefined)[];
        try {
            chunkResults = await Promise.all(
                offsets.map(offset => getCollectionProducts(buildInput(offset), this.props.context.actionContext).catch(() => undefined))
            );
        } catch {
            return;
        }

        const all: ProductSearchResult[] = [];
        let totalCount = listPageState.totalProductCount ?? 0;
        for (const result of chunkResults) {
            if (result?.products?.length) {
                all.push(...result.products);
            }
            if (typeof result?.count === 'number') {
                totalCount = result.count;
            }
        }

        if (!all.length) {
            return;
        }

        const uniqueProducts = Array.from(new Map(all.map(product => [product.RecordId, product])).values());

        transaction(() => {
            listPageState.activeProducts = uniqueProducts;
            listPageState.totalProductCount = Math.max(totalCount, uniqueProducts.length);
            listPageState.currentPageNumber = Math.floor(uniqueProducts.length / this._getItemsPerPage());
        });

        if (this.props.config.enableAffiliationBasedPricing) {
            void this._getActivePrice();
        }
        void this.getNonClubPlusDiscountPrices();
    };

    private readonly _getScrollStorageKey = (): string => {
        return `plp-scroll-${window.location.pathname}`;
    };

    private readonly _getScrollProductKey = (): string => {
        return `plp-scroll-product-${window.location.pathname}`;
    };

    private readonly _getProductsCacheKey = (): string => {
        return `plp-products-${window.location.pathname}${window.location.search}`;
    };

    private readonly _saveScrollOnProductClick = (event: Event): void => {
        const target = event.target as HTMLElement | null;
        if (!target || typeof target.closest !== 'function') {
            return;
        }
        // Only persist when the click is on a link (navigating away to a product).
        const link = target.closest('a');
        if (!link) {
            return;
        }
        try {
            const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
            sessionStorage.setItem(this._getScrollStorageKey(), scrollY.toString());

            const productEl = target.closest('.msc-product');
            const recordId = productEl?.querySelector('a.msc-product__url')?.getAttribute('aria-label');
            if (recordId) {
                sessionStorage.setItem(this._getScrollProductKey(), recordId);
            } else {
                sessionStorage.removeItem(this._getScrollProductKey());
            }

            // Cache the already-loaded products so returning is instant (no re-fetch).
            const lps = this.props.data.listPageState.result;
            if (lps?.activeProducts?.length) {
                const payload = JSON.stringify({
                    products: lps.activeProducts,
                    totalCount: lps.totalProductCount ?? lps.activeProducts.length,
                    pageNumber: lps.currentPageNumber ?? 0
                });
                sessionStorage.setItem(this._getProductsCacheKey(), payload);
            }
        } catch {
            // ignore storage errors (e.g. private mode / quota exceeded); API fallback will handle restore.
        }
    };

    private readonly _restoreProductsFromCache = async (): Promise<boolean> => {
        if (!MsDyn365.isBrowser || !this._isBackForwardNavigation()) {
            return false;
        }
        let raw: string | null = null;
        try {
            raw = sessionStorage.getItem(this._getProductsCacheKey());
        } catch {
            return false;
        }
        if (!raw) {
            return false;
        }
        let cached: { products: ProductSearchResult[]; totalCount: number; pageNumber: number } | undefined;
        try {
            cached = JSON.parse(raw) as { products: ProductSearchResult[]; totalCount: number; pageNumber: number };
        } catch {
            return false;
        }
        if (!cached?.products?.length) {
            return false;
        }
        const listPageState = await this.props.data.listPageState;
        if (!listPageState) {
            return false;
        }

        const products = cached.products;
        const totalCount = cached.totalCount;
        const pageNumber = cached.pageNumber;
        transaction(() => {
            listPageState.activeProducts = products;
            listPageState.totalProductCount = Math.max(totalCount || 0, products.length);
            listPageState.currentPageNumber =
                typeof pageNumber === 'number' ? pageNumber : Math.floor(products.length / this._getItemsPerPage());
        });

        if (this.props.config.enableAffiliationBasedPricing) {
            void this._getActivePrice();
        }
        void this.getNonClubPlusDiscountPrices();

        // Clear the products cache once consumed (the next product click re-saves it).
        try {
            sessionStorage.removeItem(this._getProductsCacheKey());
        } catch {
            // ignore
        }
        return true;
    };

    private readonly _restoreScrollPosition = (): void => {
        if (!MsDyn365.isBrowser || !window) {
            return;
        }
        // Only restore on back/forward navigation, not on a fresh visit to the page.
        if (!this._isBackForwardNavigation()) {
            return;
        }

        let savedProductId: string | null = null;
        let savedY: string | null = null;
        try {
            savedProductId = sessionStorage.getItem(this._getScrollProductKey());
            savedY = sessionStorage.getItem(this._getScrollStorageKey());
        } catch {
            return;
        }
        const targetY = savedY ? parseInt(savedY, 10) : NaN;
        if (!savedProductId && (isNaN(targetY) || targetY <= 0)) {
            return;
        }

        const startTime = Date.now();
        const maxDurationMs = 5000;
        const stabilizeMs = 400;
        let cancelled = false;
        let jumped = false;
        let jumpTime = 0;

        const cleanup = (): void => {
            if (this._scrollReapplyRaf !== null) {
                window.cancelAnimationFrame(this._scrollReapplyRaf);
                this._scrollReapplyRaf = null;
            }
            window.removeEventListener('wheel', onUserInteract, true);
            window.removeEventListener('touchstart', onUserInteract, true);
            window.removeEventListener('keydown', onUserInteract, true);
            try {
                sessionStorage.removeItem(this._getScrollStorageKey());
                sessionStorage.removeItem(this._getScrollProductKey());
            } catch {
                // ignore
            }
        };

        const onUserInteract = (): void => {
            cancelled = true;
            cleanup();
        };

        window.addEventListener('wheel', onUserInteract, true);
        window.addEventListener('touchstart', onUserInteract, true);
        window.addEventListener('keydown', onUserInteract, true);

        const findProductEl = (): HTMLElement | null => {
            if (!savedProductId) {
                return null;
            }
            const link = document.querySelector(`a.msc-product__url[aria-label="${savedProductId}"]`);
            return (link?.closest('.msc-product') as HTMLElement | null) || (link as HTMLElement | null);
        };

        const applyToProduct = (el: HTMLElement): void => {
            const rect = el.getBoundingClientRect();
            const desired = window.pageYOffset + rect.top - Math.max((window.innerHeight - rect.height) / 2, 0);
            const maxScrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
            window.scrollTo(0, Math.min(Math.max(desired, 0), maxScrollable));
        };

        const step = (): void => {
            if (cancelled) {
                return;
            }
            const elapsed = Date.now() - startTime;

            if (!jumped) {
                const productEl = findProductEl();
                if (productEl) {
                    // Preferred path: the product element exists -> jump straight to it.
                    applyToProduct(productEl);
                    jumped = true;
                    jumpTime = Date.now();
                } else if (!savedProductId || elapsed >= maxDurationMs) {
                    // Fallback path: no product id, or it never appeared -> use the saved pixel offset
                    // once the page is tall enough (or on timeout).
                    const maxScrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
                    if (!isNaN(targetY) && targetY > 0 && (maxScrollable >= targetY || elapsed >= maxDurationMs)) {
                        window.scrollTo(0, Math.min(targetY, maxScrollable));
                        jumped = true;
                        jumpTime = Date.now();
                    }
                }
            } else {
                // Re-assert briefly to absorb late layout shifts (images loading) then stop.
                const productEl = findProductEl();
                if (productEl) {
                    applyToProduct(productEl);
                }
                if (Date.now() - jumpTime >= stabilizeMs) {
                    cleanup();
                    return;
                }
            }

            if (elapsed >= maxDurationMs && jumped) {
                cleanup();
                return;
            }
            this._scrollReapplyRaf = window.requestAnimationFrame(step);
        };

        this._scrollReapplyRaf = window.requestAnimationFrame(step);
    };

    /** Returns true when the page was reached via browser back/forward navigation. */
    private readonly _isBackForwardNavigation = (): boolean => {
        try {
            const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
            if (navEntries && navEntries.length > 0) {
                return navEntries[0].type === 'back_forward';
            }
        } catch {
            // ignore
        }
        return false;
    };

    public render(): JSX.Element {
        const { imageSettings, className } = this.props.config;
        const { resources } = this.props;
        // VSI-Customization START - check empty product object on searching special character.
        const products =
            (this.props.data.listPageState.result &&
            this.props.data.listPageState.result.activeProducts &&
            this.props.data.listPageState.result.activeProducts.length === 1 &&
            !this.props.data.listPageState.result.activeProducts[0].ItemId
                ? []
                : this.props.data.listPageState.result!.activeProducts) || [];
        // VSI-Customization END
        const searchProduct = (this.props.data.listPageState.result && this.props.data.listPageState.result.featureProduct) || null;
        let errorText = '';
        if (!products || products.length === 0) {
            errorText = this._pageType === 'Category' ? resources.resultCategoryNotFoundText : resources.resultSearchNotFoundText;
        }
        const productsComponent = this._getProducts();
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
            />
        );

        const searchResultContainerViewProps = {
            ...this.props,

            products: productsComponent,
            TitleViewProps: !this.props.config.disableHierarchy && this._getCollectionTitle(),
            categoryHierarchy: !this.props.config.disableHierarchy && this._getCategoryHierarchy(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container new-filter-ms-search-result-container test-3', className)
            },
            sortByOptions:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0
                    ? this._getSortingDropDown()
                    : null,
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            CategoryNavContainer: { className: 'ms-search-result-container__category-nav-section new-filter__category-nav-section' },
            RefineAndProductSectionContainer: {
                className: 'ms-search-result-container__refine-product-section new-filter__refine-product-section'
            },
            choiceSummary: this._getChoiceSummary(),
            modalToggle:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0 ? (
                    <ModalToggle
                        text={resources.modalTitle}
                        ariaLabel={resources.modalTitle}
                        innerRef={this._modalToggleRef}
                        onClick={this._toggleModal}
                        id='search-result-modal'
                    />
                ) : null,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct:
                this.props.data.listPageState.result &&
                this.props.data.listPageState.result.featureProduct !== null &&
                this.props.data.listPageState.result.featureProduct !== undefined
                    ? featureComponent
                    : null,
            productPrices: this.productPrices,
            refinersRef: this.refinersRef,
            onMoreFiltersClicked: this.toggleRefinersInSameColumn,
            totalProductCount: this.props.data.listPageState.result?.totalProductCount || 0,
            onClearFilters: this._onClearFilters,
            selectedRefiners: (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || [],
            hasRangeValidationError: this.state.hasRangeValidationError,
            selectedSortOption: this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption
        };
        return this.props.renderView(searchResultContainerViewProps) as React.ReactElement;
    }

    private readonly _getActivePrice = async (): Promise<void> => {
        const priceAffiliations = [
            {
                AffiliationId: this.props.context.app.config.AffiliationId,
                LoyaltyTierId: this.props.context.app.config.LoyaltyTierId,
                AffiliationTypeValue: this.props.context.app.config.AffiliationTypeValue,
                ReasonCodeLines: [],
                CustomerId: '',
                ExtensionProperties: []
            }
        ];

        let products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const productRecIds = ArrayExtensions.hasElements(products) ? [...new Set(products.map(prod => prod.RecordId))] : [];

        if (!productRecIds.length) {
            return;
        }
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };

        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            priceAffiliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                this.productPrices = activePricesResponse;
                const discountLines = activePricesResponse.map(priceResp => priceResp.DiscountLines);
                const prodIds: number[] = [];
                activePricesResponse.map(priceResp => {
                    if (priceResp.ProductId) {
                        prodIds.push(priceResp.ProductId);
                    }
                });
                if (this.checkDifference(prodIds, this.state.updatedProducts)) {
                    this.setState({
                        discountLines: discountLines,
                        showProdPrice: true,
                        updatedProducts: prodIds
                    });
                }

                products = products.map(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(
                            activePrice.BasePrice || 0,
                            activePrice.TradeAgreementPrice || 0,
                            activePrice.AdjustedPrice || 0
                        );
                    }
                    return prod;
                });

                products = products?.filter(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        return true;
                    }
                    return false;
                });
                this.props.data.listPageState.then(listPageState => {
                    listPageState.activeProducts = products;
                });
            })
            .catch((error: Error) => {
                this.setState({
                    showProdPrice: true
                });
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };

    private readonly getNonClubPlusDiscountPrices = async (): Promise<void> => {
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        if (!productRecIds.length) {
            return;
        }
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        try {
            let activePricesResponse: ProductPrice[] = await getActivePricesAsync(
                {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: {}
                },
                projectDomain,
                productRecIds,
                new Date(),
                null,
                null,
                true
            );
            if (activePricesResponse && activePricesResponse.length > 0) {
                activePricesResponse = activePricesResponse.filter(prod => {
                    const activePrice = products?.find(price => price.RecordId === prod.ProductId);
                    if (activePrice) {
                        return true;
                    }
                    return false;
                });
                this.setState({
                    nonClubPlusDiscount: activePricesResponse
                });
            }
        } catch (error) {
            this.setState({
                showProdPrice: true
            });
            if (error instanceof Error) {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
            }
            this.props.context.actionContext.telemetry.debug(
                '[get Affiliation based price on search result container ]Error executing action'
            );
            throw new Error('[get Affiliation based price on search result container]Error executing action');
        }
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private readonly _getCollectionTitle = (): ITitleViewProps => {
        const { data, context, resources } = this.props;

        let collectionTitle: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q) {
            collectionTitle = `"${context.request.query.q}"`;
        } else {
            collectionTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        let productCountText = '';
        let productCountNumber: number | undefined;
        if (data.listPageState && data.listPageState.result && data.listPageState.result.totalProductCount !== undefined) {
            productCountNumber = data.listPageState.result.totalProductCount;
        } else if (data.products && data.products.result) {
            productCountNumber = data.products.result.count;
        }

        if (productCountNumber && productCountNumber !== 0) {
            productCountText =
                productCountNumber !== 1
                    ? format(this.props.resources.numberOfProducts, productCountNumber)
                    : this.props.resources.oneProduct;
        } else {
            productCountText = format(this.props.resources.numberOfProducts, 0);
        }
        const isSearchPage = this.props.context.request.query?.q;
        const pageName = context.request.telemetryPageName ? `result-count-${context.request.telemetryPageName.toLowerCase()}` : '';
        const titlePrefix = <Title className='ms-search-result__collection-title-prefix' text={resources.searchTextPrefix} />;
        const titleText = collectionTitle && <Title className='ms-search-result__collection-title-text' text={collectionTitle} />;
        const titleCount = <Title className='ms-search-result__collection-title-count' text={productCountText} />;

        return {
            TitleContainer: { className: `ms-search-result-container__title ${collectionTitle ? '' : 'd-none'} ${pageName}` },
            title: {
                titlePrefix,
                titleText,
                titleCount: isSearchPage ? titleCount : undefined
            }
        };
    };

    private readonly _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const existingCategoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return (
                    <Link
                        key={index}
                        text={value.Name}
                        ariaLabel={`${this.props.resources.categoryLinkAriaLabel} ${value.Name}`}
                        href={value.Url}
                    />
                );
            });

        const categoryLinks = [...(existingCategoryLinks || [])];
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    private readonly _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = this.props;
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdwonOptions = this.sortByDropdownOptions;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config.hideRating) {
            dropdwonOptions = this.sortByDropdownOptions.filter(dropdownOption => dropdownOption.key !== 'AverageRating-Desc');
        }
        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                labelText={resources.sortByDropdownLabel}
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={dropdwonOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown,
            sortOptions: dropdwonOptions,
            selectedSortOption: activeDropdown,
            onSortOptionSelect: this._handleSortOptionSelect
        };
    };
    private readonly _onLoadMore = async (): Promise<void> => {
        const listPageState = this.props.data.listPageState.result;

        if (!listPageState) {
            return;
        }

        if (this.state.isLoadMoreLoading) {
            return;
        }

        this.setState({
            isLoadMoreLoading: true,
            isLoadMoreTriggered: true
        });

        try {
            const itemsPerPage = this._getItemsPerPage();

            const currentLoadedCount = listPageState.activeProducts?.length || 0;

            if (this.state.isLoadMoreTriggered) {
                return;
            }
            const input = new GetFullProductsByCollectionInput(
                listPageState.pageType,
                this.props.context.request.apiSettings,
                {
                    Paging: {
                        Top: itemsPerPage,
                        Skip: currentLoadedCount
                    },
                    count: true,
                    Sorting: listPageState.sortingCritera || {}
                },
                listPageState.activeFilters || [],
                +(this.props.context.request.urlTokens.itemId || 0),

                this.props.context.request.query?.q
                    ? this.props.context.request.query.q
                    : JSON.stringify({
                          ProductId: this.props.context.request.query?.productId,
                          Recommendation: this.props.context.request.query?.recommendation
                      }),
                !!(
                    this.props &&
                    this.props.config &&
                    this.props.config.includeAttributes !== undefined &&
                    this.props.config.includeAttributes
                ),
                this.props.config.updateRefinerPanel,
                this.props.context.request.locale
            );

            const productResults = await getCollectionProducts(input, this.props.context.actionContext);

            if (!productResults?.products?.length || productResults.products.length === 0) {
                return;
            }
            transaction(() => {
                const existingProducts = listPageState.activeProducts || [];

                const mergedProducts = [...existingProducts, ...productResults.products];

                const uniqueProducts = Array.from(new Map(mergedProducts.map(product => [product.RecordId, product])).values());

                listPageState.activeProducts = uniqueProducts;

                // Calculate count based on actual products we have
                // Use API count only if it's greater (indicates more products to load)
                const actualProductsCount = listPageState.activeProducts?.length || 0;
                const apiCount = productResults.count ?? 0;
                listPageState.totalProductCount = Math.max(apiCount, actualProductsCount);

                listPageState.currentPageNumber = (listPageState.currentPageNumber || 0) + 1;
            });
            let url;
            if (MsDyn365.isBrowser && window) {
                url = new URL(window.location.href);

                url.searchParams.set('skip', currentLoadedCount.toString());

                window.history.replaceState({}, '', url.toString());
            }

            if (this.props.config.enableAffiliationBasedPricing && listPageState.activeProducts?.length) {
                await this._getActivePrice();
            }
            await this.getNonClubPlusDiscountPrices();
        } catch (error) {
            console.error('LOADMORE ERROR =', error);
        } finally {
            this.setState({
                isLoadMoreLoading: false,
                isLoadMoreTriggered: false
            });
        }
    };
    private readonly _getPagination = (): React.ReactNode => {
        const listPageState = this.props.data.listPageState.result;

        if (!listPageState) {
            return null;
        }

        const totalItems = listPageState.totalProductCount || 0;

        const viewedItems = listPageState.activeProducts.length;

        const allLoaded = viewedItems >= totalItems;

        return (
            <div className='load-more-wrapper'>
                <p>
                    You&apos;ve viewed <b>{viewedItems}</b> out of <b>{totalItems}</b> {totalItems === 1 ? 'result' : 'results'}.
                </p>

                {!allLoaded && (
                    <>
                        {this.state.isLoadMoreLoading ? (
                            <Waiting />
                        ) : (
                            <button className='load-more-btn' type='button' onClick={this._onLoadMore}>
                                Load more
                            </button>
                        )}
                    </>
                )}
            </div>
        );
    };

    private toggleRefinersInSameColumn = (keyName: string, isExpanded: boolean, isFromMoreFilters?: boolean) => {
        if (this.state.refinersExpandedStateData && this.state.refinersExpandedStateData.length) {
            const expandedStateData: IDobbiesRefinerExpandedState[][] = [];
            this.state.refinersExpandedStateData.map(refRow => {
                expandedStateData.push(refRow);
            });
            if (isFromMoreFilters && expandedStateData && expandedStateData.length) {
                let hasFilterOpened = false;
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        if (!expandedStateData[i][j].isExpanded) {
                            hasFilterOpened = true;
                        }
                    }
                }
                if (!hasFilterOpened) {
                    return;
                }
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        expandedStateData[i][j].isExpanded = true;
                    }
                }
                this.setState({ refinersExpandedStateData: expandedStateData });
            } else if (expandedStateData && expandedStateData.length) {
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        if (expandedStateData[i][j] && expandedStateData[i][j].keyName === keyName) {
                            expandedStateData[i][j].isExpanded = isExpanded;
                        }
                        if (expandedStateData[i][j].keyName === keyName && !isExpanded) {
                            for (let k = 0; k < expandedStateData.length; k++) {
                                for (let m = 0; m < expandedStateData[k].length; m++) {
                                    if (expandedStateData[k][m] && expandedStateData[k][m].keyName !== keyName) {
                                        expandedStateData[k][m].isExpanded = true;
                                    }
                                }
                            }
                        }
                    }
                }
                this.setState({ refinersExpandedStateData: expandedStateData });
            }
        }
    };

    /**
     * Handles expand/collapse toggle for any refiner.
     * Updates the simple Map-based expanded state.
     */
    private handleRefinerExpandToggle = (keyName: string | undefined, isExpanded: boolean) => {
        if (!keyName) {
            return;
        }

        this.setState(prevState => {
            const newMap = new Map(prevState.refinerExpandedStates);
            newMap.set(keyName, isExpanded);
            return { refinerExpandedStates: newMap };
        });

        // Also update the legacy refinersExpandedStateData for backward compatibility
        this.toggleRefinersInSameColumn(keyName, isExpanded, false);
    };

    /**
     * Gets the expanded state for a refiner.
     * Returns true by default if no state is stored.
     */
    private getRefinerExpandedState = (keyName: string | undefined): boolean => {
        if (!keyName) {
            return true;
        }
        const state = this.state.refinerExpandedStates.get(keyName);
        return state !== undefined ? state : true; // Default to expanded
    };

    /**
     * Syncs refinersExpandedStateData with the current set of refiners.
     * Preserves expanded state for refiners that still exist, defaults new refiners to expanded.
     */
    private syncRefinersExpandedState = (endingRefiners: IProductRefinerHierarchy[] | undefined) => {
        if (!endingRefiners || endingRefiners.length === 0) {
            return;
        }

        // Build a map of current expanded states by keyName for quick lookup
        const currentExpandedStates = new Map<string, boolean>();
        if (this.state.refinersExpandedStateData) {
            for (const row of this.state.refinersExpandedStateData) {
                for (const entry of row) {
                    if (entry.keyName) {
                        currentExpandedStates.set(entry.keyName, entry.isExpanded);
                    }
                }
            }
        }

        // Build new state data preserving existing expanded states
        const maxRefinersColumns = this._getViewport() !== 'xl' && this._getViewport() !== 'lg' ? 4 : 5;
        const filtersNavData: (string | undefined)[][] = [];
        let filtersRow: (string | undefined)[] = [];

        for (let index = 0; index < endingRefiners.length; index++) {
            if (index % maxRefinersColumns === 0 && index !== 0) {
                filtersNavData.push(filtersRow);
                filtersRow = [];
            }
            filtersRow.push(endingRefiners[index].KeyName);
        }
        if (filtersRow.length) {
            filtersNavData.push(filtersRow);
        }

        const refinersExpandedStateData: IDobbiesRefinerExpandedState[][] = [];
        for (let i = 0; i < filtersNavData.length; i++) {
            const refinersRowExpandedStates: IDobbiesRefinerExpandedState[] = [];
            for (let j = 0; j < filtersNavData[i].length; j++) {
                const filtersKeyName = filtersNavData[i][j];
                // Preserve existing expanded state if available, otherwise default to true
                const isExpanded = filtersKeyName ? currentExpandedStates.get(filtersKeyName) ?? true : true;
                refinersRowExpandedStates.push({ isExpanded, keyName: filtersKeyName });
            }
            if (refinersRowExpandedStates.length) {
                refinersExpandedStateData.push(refinersRowExpandedStates);
            }
        }

        this.setState({ refinersExpandedStateData });
    };

    /**
     * Checks if the current refiners differ from what's stored in refinersExpandedStateData
     */
    private refinersHaveChanged = (endingRefiners: IProductRefinerHierarchy[] | undefined): boolean => {
        if (!endingRefiners || !this.state.refinersExpandedStateData) {
            return false;
        }

        // Get current refiner keyNames from state
        const stateKeyNames = new Set<string>();
        for (const row of this.state.refinersExpandedStateData) {
            for (const entry of row) {
                if (entry.keyName) {
                    stateKeyNames.add(entry.keyName);
                }
            }
        }

        // Get current refiner keyNames from endingRefiners
        const currentKeyNames = new Set(endingRefiners.map(r => r.KeyName).filter(Boolean) as string[]);

        // Check if sets are different
        if (stateKeyNames.size !== currentKeyNames.size) {
            return true;
        }
        for (const keyName of currentKeyNames) {
            if (!stateKeyNames.has(keyName)) {
                return true;
            }
        }
        return false;
    };

    private setInitialSameColumnRefinerDetails = (endingRefiners: IProductRefinerHierarchy[] | undefined) => {
        const maxRefinersColumns = this._getViewport() !== 'xl' && this._getViewport() !== 'lg' ? 4 : 5;
        const filtersNavData: (string | undefined)[][] = [];
        let filtersRow: (string | undefined)[] = [];
        if (endingRefiners) {
            for (let index = 0; index < endingRefiners.length; index++) {
                if (index % maxRefinersColumns === 0 && index !== 0) {
                    filtersNavData.push(filtersRow);
                    filtersRow = [];
                }
                filtersRow.push(endingRefiners[index].KeyName);
            }
            if (filtersRow.length) {
                filtersNavData.push(filtersRow);
            }
            const refinersExpandedStateData: IDobbiesRefinerExpandedState[][] = [];
            if (filtersNavData && filtersNavData.length) {
                for (let i = 0; i < filtersNavData.length; i++) {
                    const refinersRowExpandedStates: IDobbiesRefinerExpandedState[] = [];
                    for (let j = 0; j < filtersNavData[i].length; j++) {
                        const filtersKeyName = filtersNavData[i][j];
                        refinersRowExpandedStates.push({ isExpanded: true, keyName: filtersKeyName });
                    }
                    if (refinersRowExpandedStates.length) {
                        refinersExpandedStateData.push(refinersRowExpandedStates);
                    }
                }
            }
            this.setState({ refinersExpandedStateData: refinersExpandedStateData });
        }
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context, config } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const validRefiners =
            this.props.config.updateRefinerPanel && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  })
                : data.refiners.result &&
                  data.refiners.result.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  });

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);

        const sortedRefiners = validRefiners;

        const startingRefinerList = this.props.config.startingRefinerList || [];

        const startingRefiners = sortedRefiners?.filter(refiner => startingRefinerList.indexOf(refiner.KeyName!) > -1);

        let endingRefiners = sortedRefiners?.filter(refiner => {
            const existsInStarting = startingRefiners?.find(startingRefiner => startingRefiner.RecordId === refiner.RecordId);

            return !existsInStarting;
        });

        if (this.props.context.app.config.hideRating) {
            endingRefiners = endingRefiners?.filter(er => er.SourceValue !== ProductRefinerSource.Rating);
        }

        const expandedRefiners = this.expandrefinerCount;

        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={productRefinerHierarchy.KeyName || productRefinerHierarchy.RecordId || index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        refinerSearchThreshold={this.props.config.refinerSearchThreshold}
                        refinersWithoutSearch={this.props.config.refinersWithoutSearch}
                        onRangeValidationErrorChange={this._onRangeValidationErrorChange}
                        controlledExpanded={this.getRefinerExpandedState(productRefinerHierarchy.KeyName)}
                        onExpandChange={this.handleRefinerExpandToggle}
                    />
                );
            });

        const endingSubMenus =
            endingRefiners &&
            endingRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={productRefinerHierarchy.KeyName || productRefinerHierarchy.RecordId || index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        refinersExpandedStateData={this.state.refinersExpandedStateData}
                        onToggle={this.toggleRefinersInSameColumn}
                        refinerSearchThreshold={this.props.config.refinerSearchThreshold}
                        refinersWithoutSearch={this.props.config.refinersWithoutSearch}
                        onRangeValidationErrorChange={this._onRangeValidationErrorChange}
                        controlledExpanded={this.getRefinerExpandedState(productRefinerHierarchy.KeyName)}
                        onExpandChange={this.handleRefinerExpandToggle}
                    />
                );
            });
        // Defer setState to avoid calling it during render
        // Initial setup: only runs once when refinersExpandedStateData doesn't exist
        if (!this.state.refinersExpandedStateData && !this._refinersInitialized && endingRefiners && endingRefiners.length > 0) {
            this._refinersInitialized = true;
            setTimeout(() => {
                this.setInitialSameColumnRefinerDetails(endingRefiners);
            }, 0);
        } else if (this._refinersInitialized && endingRefiners && endingRefiners.length > 0 && this.refinersHaveChanged(endingRefiners)) {
            // Sync when refiners change (preserving existing expanded states)
            setTimeout(() => {
                this.syncRefinersExpandedState(endingRefiners);
            }, 0);
        }
        return {
            RefineMenuContainer: { className: `ms-search-result-container__refine-menu` },
            RefinerSectionContainer: {
                className: `ms-search-result-container__refiner-section new-filter__refiner-section`
            },
            refiners: subMenus,
            endingRefiners: endingSubMenus
        };
    };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllText}
                FilterActiveText={resources.filterActiveText}
                label={resources.choiceSummaryLabel}
                selectedChoices={selectedRefiners}
                selectedSortOption={this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption}
                // @ts-expect-error: NOTE Type-unsafe line below, null refinersByCategoryHierarchy case not handled
                refinerHierarchy={data.refiners.result}
                choiceFormat={resources.choiceFormat}
                choiceRangeValueFormat={resources.choiceRangeValueFormat}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                onSelectedSortOptionClicked={this._onSelectedSortOptionClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabel}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
                totalProductCount={data.listPageState.result?.totalProductCount || 0}
            />
        );
    };

    private readonly _getProducts = (): JSX.Element => {
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { resources } = this.props;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const quickviewslot = this.props.slots && this.props.slots.quickview && this.props.slots.quickview[0];
        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        // Use a single consistent key to prevent unmount/remount when shouldShowPLPAds changes
        return (
            <ProductSearchResultItems
                key={'search-result-items'}
                products={products}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                allowBackNavigation={allowBack}
                telemetryContent={this.telemetryContent}
                quickviewSlot={quickviewslot}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                discountLines={this.state.discountLines}
                config={this.props.config}
                showProdPrice={this.state.showProdPrice}
                wishlists={this.props.data.wishlists.result}
                isMobile={this.isMobile}
                loyalty={this.props.data.loyaltyCard.result}
                nonClubPlusDiscount={this.state.nonClubPlusDiscount && this.state.nonClubPlusDiscount}
                showTilesOnPLP={this.state.shouldShowPLPAds ? this.props.config.showTilesOnPLP : undefined}
                tilesForPLP={this.state.shouldShowPLPAds ? this.props.config.tilesForPLP : undefined}
                repeatSameTile={this.state.shouldShowPLPAds ? this.props.config.repeatSameTile : undefined}
                maxNumberOfTiles={this.state.shouldShowPLPAds ? this.props.config.maxNumberOfTiles : undefined}
                tileToAppearAfterProducts={this.state.shouldShowPLPAds ? this.props.config.tileToAppearAfterProducts : undefined}
                plpAdsSchedulingData={this.state.shouldShowPLPAds ? this.props.data.plpAdsSchedulingData.result : undefined}
                isFetchingInventory={this.state.isFetchingInventory}
            />
        );
    };

    private readonly _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        // Reset fetch guard to ensure new fetch proceeds
        this._isFetching = false;

        MsDyn365.isBrowser &&
            window &&
            window.history.pushState(
                {},
                '',
                buildListPageUrl(
                    getCurrentUrl(this.props.context.request),
                    undefined,
                    [this._getSortColumnFromSelectedOption(notification.selectedOption)],
                    undefined
                )
            );
        this.setState({ discountLines: [] });

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
            // Increment sorting version inside transaction to trigger MobX reaction after all changes
            this._sortingVersion++;
        });
    };

    private readonly _handleSortOptionSelect = (option: ILabeledDropdownOption): void => {
        this._updateSortByDropdown({ selectedOption: option });
    };

    private readonly _clearSorting = (): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            return;
        }

        // Reset fetch guard to ensure new fetch proceeds
        this._isFetching = false;

        MsDyn365.isBrowser &&
            window &&
            window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), undefined, [], undefined));
        this.setState({ discountLines: [] });

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = { Columns: [] };
            this.setState({ sortingState: { selectedSortByOption: this.sortByDropdownOptions[0], pending: false } });
            // Increment sorting version inside transaction to trigger MobX reaction after all changes
            this._sortingVersion++;
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const { data } = this.props;
        const sortCriteria = data.listPageState.result && data.listPageState.result.sortingCritera;
        const params = MsDyn365.isBrowser && window ? new URLSearchParams(window.location.search) : new URLSearchParams();

        const sortingParamValue = params.get('sorting');
        let parsedSortingParam: SortColumn[] | undefined;
        if (sortingParamValue) {
            parsedSortingParam = JSON.parse(sortingParamValue) as SortColumn[];
        }

        const activeSortColumn =
            (sortCriteria && sortCriteria.Columns && sortCriteria.Columns[0]) || (parsedSortingParam && parsedSortingParam[0]);

        if (activeSortColumn && data.searchConfiguration.result) {
            const activeMappedConfig = data.searchConfiguration.result.find(mappedSearchConfig => {
                return (
                    mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                    mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
                );
            });
            if (activeMappedConfig) {
                return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
            }
        }

        return;
    };

    /**
     * Generates a unique key for a filter selection to use in the hash table.
     * @param notification - The toggle notification containing filter info
     * @returns A unique string key
     */
    private _getFilterSelectionKey(notification: IRefineItemToggleNotification): string {
        const refinerRecordId = notification.productRefinerValue.RefinerRecordId;
        const leftValue = notification.productRefinerValue.LeftValueBoundString || '';
        const rightValue = notification.productRefinerValue.RightValueBoundString || '';
        return `${refinerRecordId}_${leftValue}_${rightValue}`;
    }

    /**
     * Generates a cache key from active filters for product count caching.
     * Sorts the filters to ensure consistent keys regardless of selection order.
     * @param activeFilters - The currently active filter values
     * @returns A unique string key for the filter combination
     */
    private _getFilterCombinationKey(activeFilters: ProductRefinerValue[] | undefined): string {
        if (!activeFilters || activeFilters.length === 0) {
            return 'NO_FILTERS';
        }
        // Sort by RefinerRecordId and LeftValueBoundString to ensure consistent keys
        const sortedFilters = [...activeFilters].sort((a, b) => {
            const aKey = `${a.RefinerRecordId}_${a.LeftValueBoundString || ''}`;
            const bKey = `${b.RefinerRecordId}_${b.LeftValueBoundString || ''}`;
            return aKey.localeCompare(bKey);
        });
        return sortedFilters.map(f => `${f.RefinerRecordId}_${f.LeftValueBoundString || ''}_${f.RightValueBoundString || ''}`).join('|');
    }

    /**
     * Merges saved refiners state with API counts for deselection.
     * Restores ALL values from saved state - this is the key for deselection to work.
     * Updates counts from API where available, keeps original saved counts otherwise.
     * @param savedRefiners - The refiners state saved in hash table (before the filter was selected)
     * @param apiRefiners - The refiners returned from API with current counts
     * @param activeFilters - Currently active/selected filter values
     * @returns Merged refiners with restored structure and updated counts
     */
    private _mergeWithApiCounts(
        savedRefiners: IProductRefinerHierarchy[],
        apiRefiners: IProductRefinerHierarchy[],
        activeFilters: ProductRefinerValue[]
    ): IProductRefinerHierarchy[] {
        if (!savedRefiners || savedRefiners.length === 0) {
            return apiRefiners || [];
        }

        return savedRefiners.map(savedRefiner => {
            // Find matching refiner in API response
            const apiRefiner = apiRefiners.find(ar => ar.RecordId === savedRefiner.RecordId || ar.KeyName === savedRefiner.KeyName);

            if (!savedRefiner.Values || savedRefiner.Values.length === 0) {
                // If saved refiner has no values, use API values
                return apiRefiner || savedRefiner;
            }

            // For range type refiners (like Price), use API bounds
            if (isRangeType(savedRefiner.DataTypeValue) && apiRefiner?.Values && apiRefiner.Values.length > 0) {
                return {
                    ...savedRefiner,
                    Values: apiRefiner.Values
                };
            }

            // DESELECTION RESTORE: Keep ALL values from saved state
            // This is the key to restoring filter options when deselecting
            const mergedValues: ProductRefinerValue[] = [];
            const addedValueKeys = new Set<string>();

            // Helper to create unique key for a value
            const getValueKey = (v: ProductRefinerValue): string => `${v.RefinerRecordId}_${v.LeftValueBoundString}`;

            // Restore ALL values from saved state
            for (const savedValue of savedRefiner.Values) {
                const valueKey = getValueKey(savedValue);

                // Find matching API value to get current count
                const apiValue = apiRefiner?.Values?.find(
                    av => av.RefinerRecordId === savedValue.RefinerRecordId && av.LeftValueBoundString === savedValue.LeftValueBoundString
                );

                // Determine count:
                // - Use API count if available (reflects current filter state)
                // - Otherwise keep the original saved count (from before the deselected filter was applied)
                let count = savedValue.Count ?? 0;
                if (apiValue) {
                    count = apiValue.Count ?? 0;
                }
                // If not in API but we have a saved count, keep the saved count
                // This preserves the original counts from before the filter was selected

                // Include ALL values from saved state - this is the key to restoring on deselection
                mergedValues.push({
                    ...savedValue,
                    Count: count
                });
                addedValueKeys.add(valueKey);
            }

            // Also add any API values that weren't in the saved state but have count > 0
            if (apiRefiner?.Values) {
                for (const apiValue of apiRefiner.Values) {
                    const valueKey = getValueKey(apiValue);
                    if (!addedValueKeys.has(valueKey) && (apiValue.Count ?? 0) > 0) {
                        mergedValues.push(apiValue);
                        addedValueKeys.add(valueKey);
                    }
                }
            }

            return {
                ...savedRefiner,
                Values: mergedValues
            };
        });
    }

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        // Reset fetch guard to ensure new fetch proceeds and count is updated
        this._isFetching = false;

        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || []
        );

        // Generate unique key for this filter selection
        const selectionKey = this._getFilterSelectionKey(itemToggleNotification);
        const filterGroupKeyName = itemToggleNotification.parentProductRefinerHierarchy?.KeyName;
        const isSelecting = itemToggleNotification.isSelecting;

        // Clear any previous pending deselection
        this._pendingDeselection = null;

        if (isSelecting) {
            // SELECTION: Save current refiners state and product count to hash table before updating
            const currentRefiners = this.state.refiners.length > 0 ? this.state.refiners : this.props.data.refiners.result || [];
            const currentProductCount = this.props.data.listPageState.result?.totalProductCount ?? 0;

            // Deep clone the refiners to preserve the state
            const refinersClone = JSON.parse(JSON.stringify(currentRefiners));
            this._refinersStateHistory.set(selectionKey, {
                refiners: refinersClone,
                productCount: currentProductCount
            });

            // Track the currently interacting filter group for preserving sibling values
            if (filterGroupKeyName && filterGroupKeyName !== this.state.currentInteractingFilterGroup) {
                this.setState({
                    currentInteractingFilterGroup: filterGroupKeyName,
                    refinersSnapshot: currentRefiners,
                    pendingDeselectionKey: null
                });
            } else {
                this.setState({ pendingDeselectionKey: null });
            }
        } else {
            // DESELECTION: Check if we have saved state for this filter value
            const savedStateEntry = this._refinersStateHistory.get(selectionKey);

            if (savedStateEntry) {
                // Remove the saved state from history as it's being used
                this._refinersStateHistory.delete(selectionKey);

                // Set class property for synchronous access in reaction handler
                this._pendingDeselection = {
                    key: selectionKey,
                    savedRefiners: savedStateEntry.refiners,
                    savedProductCount: savedStateEntry.productCount
                };

                // Also update state for consistency
                this.setState({
                    pendingDeselectionKey: selectionKey,
                    refinersSnapshot: savedStateEntry.refiners
                });
            } else {
                // No saved state, clear the interaction tracking
                this.setState({
                    pendingDeselectionKey: null,
                    currentInteractingFilterGroup: null,
                    refinersSnapshot: []
                });
            }
        }

        // If all filters are cleared, reset everything including hash table and restore initial product count
        if (updatedRefinementCriteria.length === 0) {
            this._isFetching = false;
            this._refinersStateHistory.clear();
            this._pendingDeselection = null;
            // Restore initial product count when all filters are cleared
            if (this.initialTotalProductCount !== undefined && this.props.data.listPageState.result) {
                this.props.data.listPageState.result.totalProductCount = this.initialTotalProductCount;
            }
            this.setState({
                currentInteractingFilterGroup: null,
                refinersSnapshot: [],
                pendingDeselectionKey: null,
                isLoadMoreLoading: false,
                isLoadMoreTriggered: false
            });
        }

        MsDyn365.isBrowser &&
            window &&
            window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onSelectedSortOptionClicked = () => {
        this._clearSorting();
    };

    /**
     * Callback to handle range validation error state changes from the price filter.
     * @param hasError - True if there is a validation error, false otherwise.
     */
    private readonly _onRangeValidationErrorChange = (hasError: boolean): void => {
        this.setState({ hasRangeValidationError: hasError });
    };

    private readonly _onClearFilters = (): void => {
        const { data } = this.props;

        if (!data.listPageState.result) {
            return;
        }

        // Clear all filters
        const currentRefinementCriteria: ProductRefinerValue[] = [];

        // Reset fetch guard to allow new fetch to proceed
        this._isFetching = false;

        // Clear the hash table and reset state
        this._refinersStateHistory.clear();
        // Restore initial product count when all filters are cleared
        if (this.initialTotalProductCount !== undefined && data.listPageState.result) {
            data.listPageState.result.totalProductCount = this.initialTotalProductCount;
        }
        this.setState({
            currentInteractingFilterGroup: null,
            refinersSnapshot: [],
            discountLines: [],
            pendingDeselectionKey: null,
            isLoadMoreLoading: false,
            isLoadMoreTriggered: false
        });

        // Update URL - clear both filters and sorting
        const fullUrl = getCurrentUrl(this.props.context.request);
        MsDyn365.isBrowser &&
            window &&
            window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria, [], undefined));

        // Batch all MobX observable updates in a single transaction to ensure reaction fires once
        transaction(() => {
            data.listPageState.result!.currentPageNumber = 0;
            data.listPageState.result!.activeFilters = currentRefinementCriteria;
            data.listPageState.result!.sortingCritera = { Columns: [] };
            // Increment sorting version to ensure reaction fires
            this._sortingVersion++;
        });
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        let shouldClearSorting = false;

        // Reset fetch guard to ensure new fetch proceeds and count is updated
        this._isFetching = false;

        // Clear any previous pending deselection
        this._pendingDeselection = null;

        if (!notification.clearAll) {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }

            // Generate key for the deselected choice to restore state from hash table
            const deselectionKey = `${selectedChoice.RefinerRecordId}_${selectedChoice.LeftValueBoundString ||
                ''}_${selectedChoice.RightValueBoundString || ''}`;
            const savedStateEntry = this._refinersStateHistory.get(deselectionKey);

            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );

            // If we have saved state for this deselection, use it
            if (savedStateEntry) {
                this._refinersStateHistory.delete(deselectionKey);

                // Set class property for synchronous access in reaction handler
                this._pendingDeselection = {
                    key: deselectionKey,
                    savedRefiners: savedStateEntry.refiners,
                    savedProductCount: savedStateEntry.productCount
                };

                this.setState({
                    pendingDeselectionKey: deselectionKey,
                    refinersSnapshot: savedStateEntry.refiners,
                    currentInteractingFilterGroup: null
                });
            } else if (currentRefinementCriteria.length === 0) {
                // If all filters are cleared after removing this one, reset everything
                this._isFetching = false;
                this._refinersStateHistory.clear();
                // Restore initial product count when all filters are cleared
                if (this.initialTotalProductCount !== undefined && data.listPageState.result) {
                    data.listPageState.result.totalProductCount = this.initialTotalProductCount;
                }
                this.setState({
                    currentInteractingFilterGroup: null,
                    refinersSnapshot: [],
                    pendingDeselectionKey: null,
                    isLoadMoreLoading: false,
                    isLoadMoreTriggered: false
                });
            }
        } else {
            currentRefinementCriteria = [];
            shouldClearSorting = true;
            // Reset fetch guard to allow new fetch to proceed
            this._isFetching = false;
            // Clear hash table and reset state when Clear All is clicked
            this._refinersStateHistory.clear();
            this._pendingDeselection = null;
            // Restore initial product count when all filters are cleared
            if (this.initialTotalProductCount !== undefined && data.listPageState.result) {
                data.listPageState.result.totalProductCount = this.initialTotalProductCount;
            }
            this.setState({
                currentInteractingFilterGroup: null,
                refinersSnapshot: [],
                discountLines: [],
                pendingDeselectionKey: null,
                isLoadMoreLoading: false,
                isLoadMoreTriggered: false
            });

            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);
        }

        const fullUrl = getCurrentUrl(this.props.context.request);
        // Clear sorting from URL if clearAll
        MsDyn365.isBrowser &&
            window &&
            window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria, [], undefined));
        if (shouldClearSorting) {
            // Clearing sorting from URL if clearAll is clicked
            MsDyn365.isBrowser && window && window.history.pushState({}, '', buildListPageUrl(fullUrl, undefined, [], undefined));
        }
        transaction(() => {
            data.listPageState.result!.currentPageNumber = 0;
            data.listPageState.result!.activeFilters = currentRefinementCriteria;
            if (shouldClearSorting) {
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.sortingCritera = { Columns: [] };
                this.setState({ sortingState: { selectedSortByOption: this.sortByDropdownOptions[0], pending: false } });
                // Increment sorting version inside transaction to trigger MobX reaction after all changes
                this._sortingVersion++;
            }
        });
        setTimeout(() => {
            const element = document.getElementById('plp-top-placeholder');

            if (element) {
                const top = element.getBoundingClientRect().top + window.scrollY - 120;
                window.scrollTo({
                    top: top,
                    behavior: 'smooth'
                });
            }
        }, 500);
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    private readonly _getexpandRefinerCount = (validRefiners?: number, refinerCount?: expandRefinersCount): void => {
        switch (refinerCount) {
            case expandRefinersCount.all:
                this.expandrefinerCount = validRefiners;
                break;
            case expandRefinersCount.one:
                this.expandrefinerCount = 1;
                break;
            case expandRefinersCount.two:
                this.expandrefinerCount = 2;
                break;
            case expandRefinersCount.three:
                this.expandrefinerCount = 3;
                break;
            case expandRefinersCount.four:
                this.expandrefinerCount = 4;
                break;
            default:
                this.expandrefinerCount = 0;
        }
    };
}
